// SPDX-License-Identifier: MIT
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import { BlobLister } from '../listing/blob-lister';
import { buildHierarchy } from '../listing/hierarchy-builder';
import { SelectionManager } from '../listing/selection-manager';
import { DownloadManager } from '../download/download-manager';
import { ProgressSnapshot } from '../download/types';
import { KeplerIngestAdapter } from '../download/kepler-ingest-adapter';
import { wrapTo, toggleModal, addNotification } from '@kepler.gl/actions';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { AzureHierarchyNode, AzureBlobItemMeta, SelectionEntry } from '../types';
import { assessFileSizePolicy, assessTotalSize } from '../utils/policy';

// Allow only safe path characters for prefix (mirrors PREFIX_RE in config-validation)
const SAFE_PREFIX_RE = /^[A-Za-z0-9_\-/]*$/;

interface BrowserState {
  prefix: string;
  nodes: AzureHierarchyNode[];
  loading: boolean;
  error?: string;
  nextMarker?: string;
}

// SelectionManager must be instance-scoped to avoid stale/shared state across HMR or multiple mounts
// Do NOT keep it as a module-singleton.

export const AzureBrowserPane: React.FC = () => {
  const [state, setState] = useState<BrowserState>({ prefix: '', nodes: [], loading: true });
  const [pathStack, setPathStack] = useState<string[]>([]);
  const [overLimit, setOverLimit] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [serverPrefix, setServerPrefix] = useState<string | null>(null);
  // Note: aggregation state removed after refactor; hierarchy built directly from unique pages
  const [activeFilePath, setActiveFilePath] = useState<string | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [downloadMgr] = useState(() => new DownloadManager({ concurrency: 3 }));
  const [progress, setProgress] = useState<ProgressSnapshot | null>(null);
  const [showProgress, setShowProgress] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [ingestResults, setIngestResults] = useState<{ success: number; failed: number } | null>(
    null
  );
  const [showFailedExpanded, setShowFailedExpanded] = useState(false);
  const [liveRegionMsg, setLiveRegionMsg] = useState('');
  interface DownloadSummary {
    total: number;
    succeeded: number;
    failed: number;
    cancelled: number;
    issues: { path: string; kind: string; message: string }[];
  }
  const [summary, setSummary] = useState<DownloadSummary | null>(null);
  const [detailedSummary, setDetailedSummary] = useState<any | null>(null); // holds DownloadManagerSummary.errors
  const [showDebug, setShowDebug] = useState<boolean>(
    () => typeof localStorage !== 'undefined' && localStorage.getItem('azureDebug') === '1'
  );
  // Track ids of the most recent batch so we can scope per-file status display
  const [lastBatchIds, setLastBatchIds] = useState<string[]>([]);
  const lastClickedIndexRef = useRef<number | null>(null);
  // Instance-scoped selection manager to avoid stale/global state
  const selectionRef = useRef<SelectionManager | null>(null);
  if (!selectionRef.current) selectionRef.current = new SelectionManager(10);
  const selection = selectionRef.current;
  const dispatch = useDispatch();
  const theme = useTheme();
  const ingestAdapterRef = useRef<KeplerIngestAdapter | null>(null);
  // Unified notifier using kepler.gl notifications (consolidated from custom toasts)
  const notify = useCallback(
    (type: 'success' | 'warning' | 'error' | 'info', message: string) => {
      (dispatch as Dispatch)(wrapTo('map', addNotification({ type, message }) as any) as any);
    },
    [dispatch]
  );
  // IMPORTANT: use visible KeplerGl instance id 'map' (App.tsx) instead of default 'atlas-viewer'
  if (!ingestAdapterRef.current)
    ingestAdapterRef.current = new KeplerIngestAdapter(dispatch as Dispatch, 'map', {
      onWarn: (msg: string) => notify('warning', msg),
      onError: (msg: string) => notify('error', msg),
    });
  const autoIngestTriggeredRef = useRef(false);

  useEffect(() => {
    const unsub = downloadMgr.subscribe(s => setProgress(s));
    // Bridge internal user feedback dispatcher to global toasts
    const unsubMsgs = downloadMgr.subscribeMessages(msgs => {
      const latest = msgs[msgs.length - 1];
      if (!latest) return;
      // Map dispatcher kinds directly to kepler notification types
      notify(latest.kind as any, latest.text);
    });
    return () => {
      unsub();
      unsubMsgs();
    };
  }, [downloadMgr, notify]);

  // Accessible progress announcements (throttled to meaningful deltas)
  const lastAnnouncedPctRef = useRef<number>(-1);
  useEffect(() => {
    if (!progress || progress.total === 0) return;
    const pct = progress.bytesTotal
      ? (progress.bytesCompleted / (progress.bytesTotal || 1)) * 100
      : ((progress.completed + progress.failed + progress.cancelled) / progress.total) * 100;
    const rounded = Math.floor(pct / 5) * 5; // announce every 5%
    if (rounded !== lastAnnouncedPctRef.current) {
      lastAnnouncedPctRef.current = rounded;
      setLiveRegionMsg(`Azure download ${rounded}%`);
    }
    if (
      progress.total > 0 &&
      progress.completed + progress.failed + progress.cancelled === progress.total
    ) {
      setLiveRegionMsg('Azure download complete');
    }
  }, [progress]);

  const effectiveBase = useMemo(
    () => serverPrefix ?? (pathStack.length ? pathStack[pathStack.length - 1] : ''),
    [serverPrefix, pathStack]
  );

  const load = useCallback(async (prefix: string, continuation?: string, append = false) => {
    setState(s => ({ ...s, loading: true, error: undefined }));
    try {
      const lister = new BlobLister();
      const page = await lister.list({ prefix, continuation });
      // Prefer the effective prefix returned by the lister (e.g., directory-scoped SAS)
      const effectivePrefix = page.prefix ?? prefix;
      setState(prev => {
        const existingMetas: AzureBlobItemMeta[] = append
          ? prev.nodes.filter(n => !n.isDirectory && n.meta).map(n => n.meta as AzureBlobItemMeta)
          : [];
        const combined = append ? [...existingMetas, ...page.items] : page.items;
        const map = new Map<string, AzureBlobItemMeta>();
        for (const it of combined) {
          if (!map.has(it.name)) map.set(it.name, it);
        }
        const unique = Array.from(map.values());
        const nodes = buildHierarchy(unique, effectivePrefix);
        return { prefix: effectivePrefix, nodes, loading: false, nextMarker: page.nextMarker };
      });
    } catch (e) {
      setState(s => ({ ...s, loading: false, error: (e as Error).message }));
    }
  }, []);

  // Initial / directory / server prefix load
  useEffect(() => {
    load(effectiveBase);
  }, [effectiveBase, load]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Translate search into server-side prefix when it looks like a path
  useEffect(() => {
    const sRaw = debouncedSearch.trim();
    if (!sRaw) {
      if (serverPrefix !== null) setServerPrefix(null);
      return;
    }
    // Sanitize disallowed characters (strip silently) – prevents unexpected 400 from service
    const sanitized = SAFE_PREFIX_RE.test(sRaw)
      ? sRaw
      : sRaw
          .split('')
          .filter(ch => SAFE_PREFIX_RE.test(ch))
          .join('');
    if (sanitized.endsWith('/') || sanitized.includes('/')) {
      const currentDir = pathStack.length ? pathStack[pathStack.length - 1] : '';
      const rel = currentDir + sanitized;
      if (rel !== serverPrefix) setServerPrefix(rel);
    }
  }, [debouncedSearch, pathStack, serverPrefix]);

  const enterDirectory = (node: AzureHierarchyNode) => {
    if (!node.isDirectory) return;
    setPathStack(st => [...st, node.path]);
    setSearch('');
    setServerPrefix(null);
  };

  const goUp = () => {
    setPathStack(st => {
      const next = [...st];
      next.pop();
      setSearch('');
      setServerPrefix(null);
      return next;
    });
  };

  // toggleSelect replaced by inline activate logic with multi-select support

  const startDownload = () => {
    setLastError(null);
    try {
      const selected = selection.list();
      if (!selected.length) return;
      const totalBytes = selected.reduce((sum, s) => sum + (s.size || 0), 0);
      const totalAssess = assessTotalSize(totalBytes);
      if (!totalAssess.allowed) {
        notify('error', `Selection blocked: ${totalAssess.reason}`);
        return;
      }
      const ids = downloadMgr.enqueue(
        selected.map(s => ({ path: s.path, size: s.size, contentType: s.contentType }))
      );
      setLastBatchIds(ids);
      setShowProgress(true);
      autoIngestTriggeredRef.current = false; // reset for new batch
    } catch (e) {
      setLastError((e as Error).message);
    }
  };

  const cancelAll = () => {
    // Simple cancel: iterate tasks and cancel queued/running
    downloadMgr.listTasks().forEach(t => downloadMgr.cancel(t.id));
  };

  const allDone =
    progress &&
    progress.total > 0 &&
    progress.completed + progress.failed + progress.cancelled === progress.total;

  useEffect(() => {
    if (allDone) {
      const full = downloadMgr.summary();
      setSummary(full);
      setDetailedSummary(full.errors);
    }
  }, [allDone, downloadMgr]);

  const ingest = useCallback(async () => {
    try {
      const results = downloadMgr.getResults();
      if (!results.length) return;
      const ingestResultsRaw = await ingestAdapterRef.current!.ingest(results);
      const success = ingestResultsRaw.filter(r => r.success).length;
      const failed = ingestResultsRaw.length - success;
      setIngestResults({ success, failed });
      // Post-ingest UX: clear previous selection & active file to reflect loaded state
      selection.clear();
      setActiveFilePath(null);
      // Force re-render to update selection counts (ingestResults change already triggers render)
      if (failed === 0) {
        notify(
          'success',
          `Azure load complete: ${success} file${success === 1 ? '' : 's'} loaded.`
        );
      } else if (success > 0) {
        notify('warning', `Azure load partial: ${success} loaded, ${failed} failed.`);
      } else {
        notify('error', 'Azure load failed: no files ingested.');
      }
      // Auto-close Add Data modal if at least one dataset loaded successfully (streamlined UX)
      if (success > 0) {
        try {
          // wrapTo ensures action targets correct Kepler instance
          (dispatch as Dispatch)(wrapTo('map', toggleModal(null)) as any);
        } catch {
          /* silent – modal close is non-critical */
        }
      }
    } catch (e) {
      setLastError((e as Error).message);
      notify('error', `Azure ingest error: ${(e as Error).message}`);
    }
  }, [downloadMgr, notify, dispatch, selection]);

  // Auto ingest when all downloads complete (streamlines flow – removes extra click)
  useEffect(() => {
    if (allDone && !autoIngestTriggeredRef.current) {
      autoIngestTriggeredRef.current = true;
      void ingest();
    }
  }, [allDone, ingest]);

  const filteredNodes = useMemo(() => {
    if (serverPrefix !== null) return state.nodes; // server already filtered
    if (!debouncedSearch.trim()) return state.nodes;
    const q = debouncedSearch.toLowerCase();
    return state.nodes.filter(n => n.name.toLowerCase().includes(q));
  }, [debouncedSearch, state.nodes, serverPrefix]);

  // Sorting -------------------------------------------------------------
  type SortField = 'name' | 'size';
  type SortDir = 'asc' | 'desc';
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const toggleSort = (field: SortField) => {
    setSortField(prev => {
      if (prev === field) {
        setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        return prev; // keep same field
      }
      setSortDir('asc');
      return field;
    });
  };

  const sortedNodes = useMemo(() => {
    const arr = [...filteredNodes];
    arr.sort((a, b) => {
      // Directories always first regardless of sort field for usability
      if (a.isDirectory && !b.isDirectory) return -1;
      if (!a.isDirectory && b.isDirectory) return 1;
      let cmp = 0;
      if (sortField === 'name') {
        cmp = a.name.localeCompare(b.name);
      } else if (sortField === 'size') {
        const av = a.isDirectory ? -1 : (a.size ?? 0);
        const bv = b.isDirectory ? -1 : (b.size ?? 0);
        cmp = av === bv ? a.name.localeCompare(b.name) : av - bv;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filteredNodes, sortField, sortDir]);

  // File type badge helper ----------------------------------------------
  const fileTypeInfo = (name: string, contentType?: string) => {
    const lower = name.toLowerCase();
    if (lower.endsWith('.csv')) return { label: 'CSV', color: theme?.badgeCsvBg || '#1f6feb' };
    if (lower.endsWith('.parquet') || lower.endsWith('.parq') || lower.endsWith('.pq'))
      return { label: 'PARQ', color: theme?.badgeParquetBg || '#8957e5' };
    if (lower.endsWith('.json')) return { label: 'JSON', color: theme?.badgeJsonBg || '#9e6a03' };
    if (lower.endsWith('.geojson') || lower.endsWith('.topojson'))
      return { label: 'GEO', color: theme?.badgeGeoBg || '#238636' };
    if (lower.endsWith('.geoparquet'))
      return { label: 'G-PARQ', color: theme?.badgeParquetBg || '#8957e5' };
    if (contentType?.includes('parquet'))
      return { label: 'PARQ', color: theme?.badgeParquetBg || '#8957e5' };
    if (contentType?.includes('json'))
      return { label: 'JSON', color: theme?.badgeJsonBg || '#9e6a03' };
    return { label: 'FILE', color: theme?.badgeFileBg || '#57606a' };
  };

  const formatSize = (bytes?: number) => {
    if (bytes === undefined || bytes === null) return '';
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let v = bytes / 1024;
    let i = 0;
    while (v >= 1024 && i < units.length - 1) {
      v /= 1024;
      i++;
    }
    return `${v.toFixed(1)} ${units[i]}`;
  };

  const selectedPaths = new Set(selection.list().map((s: SelectionEntry) => s.path));
  const selectAllChecked = useMemo(() => {
    const visibleFiles = sortedNodes.filter(n => !n.isDirectory);
    if (!visibleFiles.length) return false;
    const current = new Set(selection.list().map((s: SelectionEntry) => s.path));
    return visibleFiles.every(f => current.has(f.path));
  }, [sortedNodes, selection]);
  const activeFile = useMemo(
    () => state.nodes.find(n => n.path === activeFilePath && !n.isDirectory),
    [activeFilePath, state.nodes]
  );

  const loadMore = useCallback(() => {
    if (state.nextMarker && !state.loading) load(state.prefix, state.nextMarker, true);
  }, [state.nextMarker, state.loading, state.prefix, load]);

  // Infinite scroll sentinel observer
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    if (!state.nextMarker) return; // nothing to load
    const obs = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) loadMore();
        });
      },
      { root: null, rootMargin: '200px', threshold: 0 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
    };
  }, [loadMore, state.nextMarker]);

  return (
    <Container data-testid='azure-browser-pane'>
      <HeaderBar>
        <HeaderTitle>Azure Container Browser</HeaderTitle>
        <NavButton
          type='button'
          onClick={() => {
            setShowDebug(d => {
              const next = !d;
              if (typeof localStorage !== 'undefined')
                localStorage.setItem('azureDebug', next ? '1' : '0');
              downloadMgr.setDebugEnabled(next);
              return next;
            });
          }}
          style={{ opacity: showDebug ? 1 : 0.5 }}
          title={showDebug ? 'Disable Azure debug logging' : 'Enable Azure debug logging'}
          data-testid='azure-debug-toggle'
        >
          {showDebug ? 'Debug On' : 'Debug Off'}
        </NavButton>
        {pathStack.length > 0 && (
          <NavButton onClick={goUp} data-testid='azure-nav-up'>
            Up
          </NavButton>
        )}
        <CurrentPath>/{pathStack.map(p => p.replace(/\/$/, '')).join('')}</CurrentPath>
        {serverPrefix && <FilterBadge>filter: {serverPrefix}</FilterBadge>}
        <SearchInput
          aria-label='Search'
          data-testid='azure-search'
          placeholder='Search or prefix (end with /)'
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </HeaderBar>
      {state.loading && <StatusText>Loading…</StatusText>}
      {state.error && <ErrorText>Error: {state.error}</ErrorText>}
      {!state.loading && !state.error && (
        <DataTable role='grid' aria-label='Azure container objects'>
          <thead>
            <tr role='row'>
              <TH $w={24}>
                <input
                  type='checkbox'
                  aria-label='Select all files on current page'
                  checked={selectAllChecked}
                  onChange={e => {
                    const checked = e.target.checked;
                    if (checked) {
                      // Add up to max remaining selections among visible files
                      const files = sortedNodes.filter(n => !n.isDirectory);
                      for (const f of files) {
                        const res = selection.add({
                          path: f.path,
                          size: f.size,
                          contentType: f.meta?.contentType,
                        });
                        if (res.overLimit) break;
                      }
                    } else {
                      // Remove all visible file selections
                      const visiblePaths = new Set(
                        sortedNodes.filter(n => !n.isDirectory).map(n => n.path)
                      );
                      selection.list().forEach((s: SelectionEntry) => {
                        if (visiblePaths.has(s.path)) selection.remove(s.path);
                      });
                    }
                    // Force refresh to reflect derived selectAll and per-row states
                    setState(s => ({ ...s }));
                  }}
                  data-testid='azure-select-all'
                />
              </TH>
              <th
                role='columnheader'
                aria-sort={
                  sortField === 'name' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
                }
                onClick={() => toggleSort('name')}
                className='sortable'
                data-testid='azure-col-name'
              >
                Name{sortField === 'name' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
              </th>
              <TH $w={70}>Type</TH>
              <th
                role='columnheader'
                aria-sort={
                  sortField === 'size' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
                }
                onClick={() => toggleSort('size')}
                className='sortable'
                data-width='90'
                data-testid='azure-col-size'
              >
                Size{sortField === 'size' ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedNodes.map(n => {
              const selected = selectedPaths.has(n.path);
              const rowIndex = sortedNodes.indexOf(n);
              const activate = (e?: React.SyntheticEvent) => {
                if (n.isDirectory) {
                  enterDirectory(n);
                  return;
                }
                // Shift-click range selection
                if (
                  e &&
                  (e as React.MouseEvent | React.KeyboardEvent).shiftKey &&
                  lastClickedIndexRef.current !== null
                ) {
                  const start = Math.min(lastClickedIndexRef.current, rowIndex);
                  const end = Math.max(lastClickedIndexRef.current, rowIndex);
                  for (let i = start; i <= end; i++) {
                    const node = sortedNodes[i];
                    if (!node.isDirectory) {
                      const res = selection.add({
                        path: node.path,
                        size: node.size,
                        contentType: node.meta?.contentType,
                      });
                      if (res.overLimit) break;
                    }
                  }
                  setState(s => ({ ...s }));
                  return;
                }
                // Enforce per-file size caps prior to toggling selection
                const alreadySelected = selectedPaths.has(n.path);
                if (!alreadySelected && !n.isDirectory && typeof n.size === 'number') {
                  const fileAssess = assessFileSizePolicy(n.name, n.size);
                  if (!fileAssess.allowed) {
                    notify('error', `Blocked: ${n.name} – ${fileAssess.reason}`);
                    return;
                  } else if (fileAssess.severity === 'warning' && fileAssess.reason) {
                    notify('warning', `${n.name}: ${fileAssess.reason}`);
                  }
                }
                const { overLimit: ol } = selection.toggle({
                  path: n.path,
                  size: n.size,
                  contentType: n.meta?.contentType,
                });
                setOverLimit(ol);
                lastClickedIndexRef.current = rowIndex;
                setActiveFilePath(p => (p === n.path ? null : n.path));
                setState(s => ({ ...s }));
              };
              return (
                <Row
                  key={n.path}
                  role='row'
                  aria-selected={selected}
                  tabIndex={0}
                  data-testid={n.isDirectory ? 'azure-dir-row' : 'azure-file-row'}
                  $selected={selected}
                  onClick={e => activate(e)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      activate(e);
                    } else if (e.key === 'ArrowDown') {
                      (e.currentTarget.nextElementSibling as HTMLElement | null)?.focus();
                    } else if (e.key === 'ArrowUp') {
                      (e.currentTarget.previousElementSibling as HTMLElement | null)?.focus();
                    }
                  }}
                >
                  <TD>
                    {!n.isDirectory && (
                      <input
                        type='checkbox'
                        aria-label={`Select file ${n.name}`}
                        checked={selected}
                        onChange={e => {
                          e.stopPropagation();
                          activate();
                        }}
                        onClick={e => e.stopPropagation()}
                        data-testid='azure-file-checkbox'
                      />
                    )}
                  </TD>
                  <TD>
                    {n.isDirectory ? '📁 ' : ''}
                    {n.name}
                  </TD>
                  <TD>
                    {n.isDirectory
                      ? 'DIR'
                      : (() => {
                          const info = fileTypeInfo(n.name, n.meta?.contentType);
                          return (
                            <FileBadge
                              $bg={info.color}
                              data-testid={`azure-filetype-${info.label}`}
                            >
                              {info.label}
                            </FileBadge>
                          );
                        })()}
                  </TD>
                  <TD>{n.isDirectory ? '' : formatSize(n.size)}</TD>
                </Row>
              );
            })}
          </tbody>
        </DataTable>
      )}
      <Sentinel ref={sentinelRef} data-testid='azure-infinite-sentinel' />
      {activeFile && (
        <MetaPanel data-testid='azure-metadata-panel'>
          <MetaTitle>Metadata</MetaTitle>
          <div>
            <strong>Name:</strong> {activeFile.name}
          </div>
          {activeFile.meta?.contentType && (
            <div>
              <strong>Type:</strong> {activeFile.meta.contentType}
            </div>
          )}
          {typeof activeFile.size === 'number' && (
            <div>
              <strong>Size:</strong> {activeFile.size.toLocaleString()} bytes
            </div>
          )}
          {activeFile.meta?.lastModified && (
            <div>
              <strong>Modified:</strong> {activeFile.meta.lastModified.toLocaleString()}
            </div>
          )}
        </MetaPanel>
      )}
      <FooterBar>
        <span>Selected: {selection.count()} / 10</span>
        {overLimit && <WarnText>Selection limit reached</WarnText>}
      </FooterBar>
      <ActionsRow>
        <button
          disabled={!selection.count()}
          onClick={startDownload}
          data-testid='azure-download-btn'
        >
          Load Selected
        </button>
        {showProgress && (
          <SecondaryButton onClick={() => setShowProgress(false)}>Hide Progress</SecondaryButton>
        )}
      </ActionsRow>
      {lastError && <ErrorInline>Download error: {lastError}</ErrorInline>}
      {showProgress && progress && (
        <ProgressPanel data-testid='azure-download-progress'>
          <MetaTitle>Download & Ingestion</MetaTitle>
          <div>
            Total: {progress.total} | Queued: {progress.queued} | Running: {progress.running} |
            Completed: {progress.completed} | Failed: {progress.failed} | Cancelled:{' '}
            {progress.cancelled}
          </div>
          {progress.bytesTotal && (
            <div>
              Bytes: {progress.bytesCompleted.toLocaleString()} /{' '}
              {progress.bytesTotal.toLocaleString()}
            </div>
          )}
          {/* Aggregate progress bar */}
          {progress.total > 0 && (
            <BlockMarginTop6>
              {(() => {
                const pct = progress.bytesTotal
                  ? (progress.bytesCompleted / (progress.bytesTotal || 1)) * 100
                  : ((progress.completed + progress.failed + progress.cancelled) / progress.total) *
                    100;
                return (
                  <ProgressBar
                    role='progressbar'
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(pct)}
                    aria-label='Overall download progress'
                    data-testid='azure-progress-bar'
                  >
                    <ProgressFill style={{ width: `${pct}%` }} />
                  </ProgressBar>
                );
              })()}
            </BlockMarginTop6>
          )}
          <ActionsRow style={{ marginTop: 6 }}>
            <button
              onClick={cancelAll}
              disabled={!progress.running && !progress.queued}
              data-testid='azure-cancel-all-btn'
            >
              Cancel All
            </button>
            {allDone && !ingestResults && <LoadingSubtle>Loading into map…</LoadingSubtle>}
          </ActionsRow>
          {allDone && summary && (
            <BlockMarginTop6 data-testid='azure-download-summary'>
              Summary: success {summary.succeeded}, failed {summary.failed}, cancelled{' '}
              {summary.cancelled}
              {summary.issues.length > 0 && <div>Issues: {summary.issues.length}</div>}
              {detailedSummary && (
                <div style={{ marginTop: 4 }}>
                  <strong>Errors</strong>:{' '}
                  {Object.keys(detailedSummary.byCategory || {}).length === 0 && 'none'}
                  {Object.entries(detailedSummary.byCategory || {}).map(([cat, count]) => {
                    const cName = String(cat);
                    const cVal = Number(count as number);
                    return (
                      <div key={cName}>
                        {cName}: {cVal}
                      </div>
                    );
                  })}
                  <div>Retry Attempts: {detailedSummary.retryAttempts}</div>
                  <div>Max Attempts Observed: {detailedSummary.maxAttemptObserved}</div>
                </div>
              )}
            </BlockMarginTop6>
          )}
          {/* Per-file status list for the last batch */}
          {lastBatchIds.length > 0 && (
            <PerFileStatusWrapper data-testid='azure-per-file-status'>
              <PerFileHeader>
                <span>Files</span>
                {summary && summary.failed > 0 && (
                  <FailedToggleButton
                    type='button'
                    onClick={() => setShowFailedExpanded(s => !s)}
                    data-testid='azure-failed-toggle'
                  >
                    {showFailedExpanded ? 'Hide Failed' : `Show Failed (${summary.failed})`}
                  </FailedToggleButton>
                )}
              </PerFileHeader>
              <PerFileTable>
                <thead>
                  <tr>
                    <PerFileTH>Name</PerFileTH>
                    <PerFileTH>Status</PerFileTH>
                    <PerFileTH>Size</PerFileTH>
                  </tr>
                </thead>
                <tbody>
                  {downloadMgr
                    .listTasks()
                    .filter(t => lastBatchIds.includes(t.id))
                    .map(t => {
                      const name = t.path.split('/').pop() || t.path;
                      let displayLabel: string = t.state;
                      let color = theme?.labelColor;
                      let icon = '';
                      switch (t.state) {
                        case 'queued':
                          icon = '⏳';
                          displayLabel = 'Queued';
                          color = theme?.subtextColor;
                          break;
                        case 'running':
                          icon = '▶';
                          displayLabel = 'Running';
                          color = theme?.primaryBtnBgd;
                          break;
                        case 'succeeded':
                          icon = '✔';
                          displayLabel = 'Loaded';
                          color = theme?.successColor || '#00a854';
                          break;
                        case 'failed':
                          icon = '✖';
                          displayLabel = 'Failed';
                          color = theme?.errorColor || '#d73527';
                          break;
                        case 'cancelled':
                          icon = '⟲';
                          displayLabel = 'Cancelled';
                          color = theme?.warningColor || '#ffa657';
                          break;
                      }
                      const isFailed = t.state === 'failed';
                      if (isFailed && summary && summary.failed > 0 && !showFailedExpanded) {
                        return null; // collapse failed rows when hidden
                      }
                      return (
                        <PerFileRow
                          key={t.id}
                          data-testid={`azure-task-${t.id}`}
                          $dimmed={t.state === 'cancelled'}
                        >
                          <PerFileCell>{name}</PerFileCell>
                          <PerFileCell>
                            <StatusTag $color={color}>
                              <span aria-hidden='true'>{icon}</span>
                              <span>{displayLabel}</span>
                            </StatusTag>
                          </PerFileCell>
                          <PerFileCell>{t.size ? formatSize(t.size) : ''}</PerFileCell>
                        </PerFileRow>
                      );
                    })}
                </tbody>
              </PerFileTable>
              {summary && summary.failed > 0 && (
                <RetryBar>
                  <RetryInfo>
                    {summary.failed} failed file{summary.failed === 1 ? '' : 's'}
                  </RetryInfo>
                  <RetryButton
                    type='button'
                    onClick={() => {
                      const failedTasks = downloadMgr
                        .listTasks()
                        .filter(t => lastBatchIds.includes(t.id) && t.state === 'failed');
                      if (!failedTasks.length) return;
                      const ids = downloadMgr.enqueue(
                        failedTasks.map(t => ({
                          path: t.path,
                          size: t.size,
                          contentType: t.contentType,
                        }))
                      );
                      setLastBatchIds(ids);
                      setShowProgress(true);
                      autoIngestTriggeredRef.current = false;
                      setShowFailedExpanded(true); // show them actively
                    }}
                    data-testid='azure-retry-failed-btn'
                  >
                    Retry Failed
                  </RetryButton>
                </RetryBar>
              )}
            </PerFileStatusWrapper>
          )}
          {ingestResults && (
            <BlockMarginTop6 data-testid='azure-ingest-summary'>
              Ingest: success {ingestResults.success}, failed {ingestResults.failed}
            </BlockMarginTop6>
          )}
        </ProgressPanel>
      )}
      {/* Live region for screen readers */}
      <LiveRegion aria-live='polite' role='status' data-testid='azure-live-region'>
        {liveRegionMsg}
      </LiveRegion>
    </Container>
  );
};

// Styled Components ------------------------------------------------------------------
const Container = styled.div`
  margin-top: 1rem;
  background: ${({ theme }) => theme.panelBackground};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 6px;
  padding: 0.75rem;
  font-size: 11px;
  color: ${({ theme }) => theme.textColor};
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const HeaderTitle = styled.strong`
  font-size: 12px;
  color: ${({ theme }) => theme.textColor};
`;

const NavButton = styled.button`
  font-size: 11px;
  padding: 2px 6px;
  background: ${({ theme }) => theme.secondaryBtnBgd};
  color: ${({ theme }) => theme.secondaryBtnColor};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 4px;
  cursor: pointer;
`;

const CurrentPath = styled.span`
  font-size: 11px;
  opacity: 0.7;
  color: ${({ theme }) => theme.subtextColor};
`;

const FilterBadge = styled.span`
  font-size: 10px;
  background: ${({ theme }) => theme.panelBackgroundHover};
  padding: 2px 4px;
  border-radius: 4px;
`;

const SearchInput = styled.input`
  margin-left: auto;
  font-size: 11px;
  padding: 2px 4px;
  background: ${({ theme }) => theme.inputBgd};
  border: 1px solid ${({ theme }) => theme.inputBorderColor};
  border-radius: 4px;
  color: ${({ theme }) => theme.inputColor};
`;

const StatusText = styled.div`
  font-size: 12px;
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.warningColor};
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  th,
  td {
    padding: 4px 4px;
  }
  th.sortable {
    cursor: pointer;
    user-select: none;
  }
`;

const TH = styled.th<{ $w?: number }>`
  ${({ $w }) => ($w !== undefined ? `width: ${$w}px;` : '')}
`;
const TD = styled.td``;

const Row = styled.tr<{ $selected: boolean }>`
  background: ${({ $selected, theme }) => ($selected ? theme.selectColor : 'transparent')};
  color: ${({ $selected, theme }) =>
    $selected ? theme.selectTextColor || theme.textColor : theme.textColor};
  cursor: pointer;
  &:focus {
    outline: ${({ theme }) =>
      `${theme.focusOutlineWidth} ${theme.focusOutlineStyle} ${theme.focusOutlineColor}`};
  }
`;

const FileBadge = styled.span<{ $bg: string }>`
  background: ${({ $bg }) => $bg};
  color: #fff;
  border-radius: 4px;
  padding: 1px 4px;
  font-size: 10px;
  letter-spacing: 0.5px;
`;

const Sentinel = styled.div`
  height: 1px;
`;

const MetaPanel = styled.div`
  margin-top: 8px;
  font-size: 11px;
  background: ${({ theme }) => theme.inputBgd};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 4px;
  padding: 8px;
`;
const MetaTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
`;

const FooterBar = styled.div`
  margin-top: 6px;
  font-size: 10px;
  display: flex;
  justify-content: space-between;
`;

const WarnText = styled.span`
  color: ${({ theme }) => theme.warningColor};
`;

const ActionsRow = styled.div`
  margin-top: 6px;
  display: flex;
  gap: 8px;
  button {
    font-size: 11px;
    padding: 4px 8px;
    background: ${({ theme }) => theme.primaryBtnBgd};
    color: ${({ theme }) => theme.primaryBtnColor};
    border: 1px solid ${({ theme }) => theme.primaryBtnBgdHover || theme.primaryBtnBgd};
    border-radius: 4px;
    cursor: pointer;
    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
`;

const SecondaryButton = styled.button`
  background: ${({ theme }) => theme.secondaryBtnBgd}!important;
  color: ${({ theme }) => theme.secondaryBtnColor}!important;
  border: 1px solid ${({ theme }) => theme.borderColor}!important;
`;

const ErrorInline = styled.div`
  color: ${({ theme }) => theme.warningColor};
  font-size: 11px;
  margin-top: 4px;
`;

const ProgressPanel = styled.div`
  margin-top: 8px;
  font-size: 11px;
  background: ${({ theme }) => theme.inputBgd};
  border: 1px solid ${({ theme }) => theme.borderColor};
  padding: 8px;
  border-radius: 4px;
`;

const ProgressBar = styled.div`
  position: relative;
  height: 8px;
  background: ${({ theme }) => theme.panelBackgroundHover};
  border-radius: 4px;
  overflow: hidden;
`;
const ProgressFill = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: ${({ theme }) => theme.primaryBtnBgd};
  transition: width 0.25s linear;
`;
const BlockMarginTop6 = styled.div`
  margin-top: 6px;
`;
const LoadingSubtle = styled.span`
  font-size: 11px;
  opacity: 0.8;
`;

// Per-file status styled components -------------------------------------------------
const PerFileStatusWrapper = styled.div`
  margin-top: 8px;
`;
const PerFileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  margin-bottom: 4px;
  font-size: 11px;
`;
const FailedToggleButton = styled.button`
  font-size: 10px;
  padding: 2px 6px;
  background: ${({ theme }) => theme.secondaryBtnBgd};
  color: ${({ theme }) => theme.secondaryBtnColor};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 4px;
  cursor: pointer;
`;
const PerFileTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 10px;
`;
const PerFileTH = styled.th`
  text-align: left;
  padding: 2px 4px;
  font-weight: 500;
`;
const PerFileRow = styled.tr<{ $dimmed: boolean }>`
  opacity: ${({ $dimmed }) => ($dimmed ? 0.6 : 1)};
`;
const PerFileCell = styled.td`
  padding: 2px 4px;
`;
const StatusTag = styled.span<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ $color }) => $color || 'inherit'};
`;
const RetryBar = styled.div`
  margin-top: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 10px;
`;
const RetryInfo = styled.span``;
const RetryButton = styled.button`
  font-size: 10px;
  padding: 2px 8px;
  background: ${({ theme }) => theme.warningColor};
  color: #000;
  border: 1px solid ${({ theme }) => theme.warningColor};
  border-radius: 4px;
  cursor: pointer;
`;

const LiveRegion = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;
